/** 
 * @description 전역 공통 팝업 관리 시스템(Alert, Confirm, Admin, Login)
 * [주요 기능]
 * 1. showAlert: 단순 알림 팝업(String 또는 Object 인자)
 * 2. showConfirm: Promise 기반의 선택형 팝업 (await 사용 가능)
 * 3. setAdminPopups: 관리자 공지 및 로컬스토리지를 통한 '오늘 하루 보지 않기' 제어
 * 4. openPopupStack: 여러 팝업이 뜰 때 최상단(topMost) 요소 판별 및 스택 관리
 * [사용 예시]
 * const { showAlert, showConfirm } = usePopup()
 * [주의 사항]
 * 모든 팝업은 App.vue(또는 전역 레이아웃)에서 단 한번만 렌더링됩니다.
 * LocalStorage 사용 시 'hide_admin_Popup_{id}' 키 형식을 유지합니다.
 */
import { ref, type Ref } from 'vue'

// 공통타입정의
export type ConfirmResult = boolean | 'etc'

// // 전역상태관리
// const isLoginPopupOpen: Ref<boolean> = ref(false)
const isLoggedIn : Ref<boolean> = ref(false)
const isTimeOut : Ref<boolean> = ref(false)
const isBefore3Min : Ref<boolean> = ref(false)
// const isAlertPopupOpen: Ref<boolean> = ref(false)
// const isConfirmPopupOpen: Ref<boolean> = ref(false)

// const useConfirmResolve = () => useState<((value: boolean) => void) | null>('confirmResolve', () => null)

// 관리자 공지 팝업 데이터 구조
export interface AdminPopup {
  id: string
  title?: string,
  content: string,
  close?: boolean,
  adClass?: string,
  // top?: number,
  // left?: number,
  width?: number,
  height?: number,
  showOnPaths?: string[] // 관리자 팝업 노출 페이지 경로 지정, 빈배열 또는 showOnPaths가 없을 경우 모든 페이지 노출
}

// Alert 팝업 데이터
interface AlertData {
  title?: string
  message: string
  confirmText?: string
  cancelButtonYn?: string
  onConfirm?: () => void
}

// Confirm 팝업 데이터
export interface ConfirmData {
  title?: string
  message?: string
  showEtcButton?: boolean
  showCancelButton?: boolean
  showConfirmButton?: boolean
  isOpen?: boolean
  confirmText?: string
  cancelText?: string
  etcText?: string
  modalClass?: string
  onConfirm?: () => void
  onCancel?: () => void
  onEtc?: () => void
}

// const confirmData: Ref<ConfirmData> = ref({
//   title: '',
//   message: '',
//   showEtcButton: false,
//   showCancelButton: false,
//   showConfirmButton: false,
//   isOpen: false,
//   confirmText: '확인',
//   cancelText: '취소',
//   etcText: '',
//   modalClass: '',
// })

// const confirmResolveRef: Ref<((value: ConfirmResult) => void) | null> = ref(null)

interface usePopupReturn {
  // 상태
  isTimeOut: Ref<boolean>
  isBefore3Min: Ref<boolean>
  isLoginPopupOpen: Ref<boolean>
  isAlertPopupOpen: Ref<boolean>
  isConfirmPopupOpen: Ref<boolean>
  isRequiredAuth : Ref<boolean>
  alertData: Ref<AlertData>
  confirmData: Ref<ConfirmData>
  isLoggedIn: Ref<boolean>
  adminPopups: Ref<AdminPopup[]>
  isLoginPopXButton: Ref<boolean>
  // 메서드
  openLoginPopup: (flag? : boolean) => void
  closeLoginPopup: () => void
  openLoginPopupReqAuth: () => void
  // alert 메서드
  showAlert: (options: string | AlertData) => void
  closeAlert: () => void

  // Confirm 메서드
  showConfirm: (optoins: ConfirmData) => Promise<ConfirmResult>
  closeConfirm: (result: boolean) => void
  handleEtc: () => void

  // admin 메서드
  setAdminPopups: (data: AdminPopup[]) => void
  closeAdminPopup: (id: string, saveToday: boolean) => void
  isAdminVisible: (id: string) => boolean

  // 열린 팝업 메서드
  // totalOpenCount: Ref<number> // 총 열린 팝업 갯수
  // isAnyPopupOpen: Ref<boolean> // 활성팝업 유무
  totalOpenCount: ComputedRef<number> // 총 열린 팝업 갯수
  isAnyPopupOpen: ComputedRef<boolean> // 활성팝업 유무

  // 팝업 증감 감소 카운트 메서드
  incrementPopupCount: () => void
  decrementPopupCount: () => void

  // 팝업 다음 인뎃스값 확인 메서드
  // getNextSequence: () => number

  isTopMost : (id: string) => boolean
  addPopupStack: (id: string) => void
  removePopupStack: (id: string) => void
  openPopupStack: Ref<string[]>
}

// 전역 공통 상태관리
const isLoginPopupOpen: Ref<boolean> = ref(false)
const isAlertPopupOpen: Ref<boolean> = ref(false)
const isConfirmPopupOpen: Ref<boolean> = ref(false)
const isRequiredAuth : Ref<boolean> = ref(false)
const isLoginPopXButton : Ref<boolean> = ref(false)
// 관리자 팝업 상태 관리
const adminPopups: Ref<AdminPopup[]> = ref([])
const closedAdminIds: Ref<string[]> = ref([])
const ADMIN_SAVE_KEY = 'hide_admin_popup_'

const alertData: Ref<AlertData> = ref({
  title: '',
  message: '',
  confirmText: '확인',
  cancelButtonYn: 'Y'
})

const confirmData: Ref<ConfirmData> = ref({
  title: '',
  message: '',
  showEtcButton: false,
  showCancelButton: false,
  showConfirmButton: false,
  isOpen: false,
  confirmText: '확인',
  cancelText: '취소',
  etcText: '',
  modalClass: '',
})

const confirmResolveRef: Ref<((value: ConfirmResult) => void) | null> = ref(null)

// 활성화된 팝업 갯수 초기화값
const activePopupCount = ref(0)

// 현재 열린 팝업 id
const openPopupStack = ref<string[]>([]);

// usePopup 메인 로직
export const usePopup = (): usePopupReturn => {
  // const confirmResolveRef = useConfirmResolve()

  // 로그인 팝업
  const openLoginPopup = (flag? : boolean): void => {
    isLoginPopupOpen.value = true
    isRequiredAuth.value = false
    if(flag){
      isLoginPopXButton.value = true;
    }
    addPopupStack('login');
  }

  const openLoginPopupReqAuth = (): void => {
    isLoginPopupOpen.value = true
    isRequiredAuth.value = true

    removePopupStack('login');
  }
  
  const closeLoginPopup = (): void => {
    isLoginPopupOpen.value = false
  }

  // 이벤트 핸들러
  let keyHandler: ((e: KeyboardEvent) => void ) | null = null

  // Alert 팝업
  const showAlert = (options: string | AlertData): void => {
    if (options === undefined) {
      return
    }
    if (typeof options === 'string') {
      alertData.value = {
        title: '알림',
        message: options,
        confirmText: '확인',
        cancelButtonYn: 'Y'
      }
    } else {
      alertData.value = {
        title: options?.title || '',
        message: options?.message,
        confirmText: options?.confirmText || '확인',
        cancelButtonYn: options?.cancelButtonYn || 'Y',
        onConfirm: options?.onConfirm
      }
    }
    isAlertPopupOpen.value = true
    
    keyHandler = (e: KeyboardEvent) => {
      if(e.key === 'Enter') {
        e.stopPropagation()
        e.stopImmediatePropagation()
        e.preventDefault()
        closeAlert()
      }
    }

    setTimeout(() => {
      if(keyHandler) {
        window.addEventListener('keyup', keyHandler, true)
      }
    }, 150);
  }

  const closeAlert = ():void => {
    if(alertData.value.onConfirm) {
      alertData.value.onConfirm()
    }
    isAlertPopupOpen.value = false

    if(keyHandler) {
      window.removeEventListener("keyup", keyHandler, true)
      keyHandler = null
    }
  }

  // Confirm 팝업(Promise 방식)
  const showConfirm = (options: ConfirmData): Promise<ConfirmResult> => {
    confirmData.value = {
      ...confirmData.value,
      title: options.title || '',
      message: options.message,
      isOpen: true,
      showEtcButton: options.showEtcButton ?? false,
      showCancelButton: options.showCancelButton ?? true,
      showConfirmButton: options.showConfirmButton ?? true,
      confirmText: options.confirmText || '확인',
      cancelText: options.cancelText || '취소',
      etcText: options.etcText || '',
      onConfirm: options.onConfirm,
      onCancel: options.onCancel,
      onEtc: options.onEtc,
      modalClass: options.modalClass || '',
    }
    isConfirmPopupOpen.value = true

    return new Promise((resolve) => {
      confirmResolveRef.value = resolve
    })
  }

  const handleEtc = (): void => {
    if (confirmData.value.onEtc) {
      confirmData.value.onEtc()
    }

    isConfirmPopupOpen.value = false
    
    if (confirmResolveRef.value) {
      (confirmResolveRef.value as (value: ConfirmResult) => void)('etc')
      confirmResolveRef.value = null
    }
  }

  const closeConfirm = (result: boolean): void => {
    if (result && confirmData.value.onConfirm) {
      confirmData.value.onConfirm()
    } else if (!result && confirmData.value.onCancel) {
      confirmData.value.onCancel()
    }

    if (confirmResolveRef.value) {
      confirmResolveRef.value(result)
      confirmResolveRef.value = null
    }

    isConfirmPopupOpen.value = false
  }

  // 관리자 팝업 기능
  const setAdminPopups = (data: AdminPopup[]): void => {
    adminPopups.value = data.filter(p => {
      // 서버 사이드 렌더링 시 localStorage 접근 에러 방지
      if (!import.meta.client) return true
      
      const expiry = localStorage.getItem(`${ADMIN_SAVE_KEY}${p.id}`)
      if (!expiry) return true
      return new Date().getTime() > parseInt(expiry)
    })
  }

  const closeAdminPopup = (id: string, saveToday: boolean): void => {
    if (saveToday && import.meta.client) {
      const expiry = new Date().getTime() + 24 * 60 * 60 * 1000
      const key = `${ADMIN_SAVE_KEY}${id}`
      localStorage.setItem(key, expiry.toString())
    }
    closedAdminIds.value.push(id)
  }

  const isAdminVisible = (id: string): boolean => !closedAdminIds.value.includes(id)

  // 팝업 카운트 증가 함수(BasePopup 컴포넌트가 마운트되거나 열릴 때 호출)
  const incrementPopupCount = () => activePopupCount.value++

  // 팝업 카운트 감소 함수(BasePopup 컴포넌트가 언마운트되거나 닫힐 때 호출)
  const decrementPopupCount = () => {
    if (activePopupCount.value > 0) activePopupCount.value--
  }

  // 총 열린 팝업 갯수 계산 함수
  const totalOpenCount = computed(() => {
    let count = 0

    if (isLoginPopupOpen.value) count++
    if (isAlertPopupOpen.value) count++
    if (isConfirmPopupOpen.value) count++

    return count + activePopupCount.value
  })

  // 활성 팝업이 있는지 확인
  const isAnyPopupOpen = computed(() => totalOpenCount.value > 0)

  // 팝업이 열리면 팝업 스택 추가
  const addPopupStack = (id: string) => {
    if (!openPopupStack.value.includes(id)) {
      openPopupStack.value.push(id)
    }
  }

  // 팝업이 닫히면 팝업 스택 제거
  const removePopupStack = (id: string) => {
    openPopupStack.value = openPopupStack.value.filter(itemId => itemId !== id)
  }

  // 팝업 스택의 맨 마지막인지 확인
  const isTopMost = (id: string) => {
    if (openPopupStack.value.length === 0) return false

    const lastId = openPopupStack.value[openPopupStack.value.length - 1]
    return lastId === id
  }


  return {
    // 상태
    isLoginPopupOpen,
    isAlertPopupOpen,
    isConfirmPopupOpen,
    isRequiredAuth,
    isLoginPopXButton,
    alertData,
    confirmData,
    adminPopups,
    isTimeOut,
    isBefore3Min,
    incrementPopupCount,
    decrementPopupCount,
    // totalOpenCount,
    // isAnyPopupOpen,
    totalOpenCount: computed(() => openPopupStack.value.length),
    isAnyPopupOpen: computed(() => openPopupStack.value.length > 0),

    // 팝업 스택 관련 함수 및 상태 반환
    addPopupStack,
    removePopupStack,
    isTopMost,
    openPopupStack,
    isLoggedIn,
    // 메서드
    openLoginPopup,
    openLoginPopupReqAuth,
    closeLoginPopup,
    showAlert,
    closeAlert,
    showConfirm,
    closeConfirm,
    handleEtc,
    setAdminPopups,
    closeAdminPopup,
    isAdminVisible
  }
}